/**
 * detect.ts — runtime platform detection.
 *
 * Runs ONCE at boot. Decisions are made by checking for the injected host
 * bridges (capability detection), never by parsing `navigator.userAgent` for
 * feature gating. userAgent is consulted only to label the OS and to pick a
 * form factor when no native bridge is present (plain web).
 *
 * See Docs/architecture/26-electron-desktop-and-mobile-architecture.md §2.1.
 */

import { resolveCapabilities, type CapabilityFlags } from "./capabilities";

export type OmniHost = "web" | "electron" | "ios" | "android";
export type FormFactor = "desktop" | "tablet" | "phone";
export type OmniOs = "windows" | "macos" | "linux" | "ios" | "android" | "unknown";

export interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface PlatformDescriptor {
  host: OmniHost;
  formFactor: FormFactor;
  isNative: boolean;
  os: OmniOs;
  safeArea: SafeAreaInsets;
  capabilities: CapabilityFlags;
}

const NO_INSETS: SafeAreaInsets = { top: 0, bottom: 0, left: 0, right: 0 };

function nodePlatformToOs(p: string): OmniOs {
  if (p === "win32") return "windows";
  if (p === "darwin") return "macos";
  if (p === "linux") return "linux";
  return "unknown";
}

function osFromUserAgent(ua: string): OmniOs {
  const s = ua.toLowerCase();
  if (/iphone|ipad|ipod/.test(s)) return "ios";
  if (/android/.test(s)) return "android";
  if (/win/.test(s)) return "windows";
  if (/mac/.test(s)) return "macos";
  if (/linux/.test(s)) return "linux";
  return "unknown";
}

/** Coarse form-factor guess from viewport + coarse-pointer media query. */
function formFactorFromViewport(win: Window | undefined): FormFactor {
  if (!win) return "desktop";
  const w = win.innerWidth || 1024;
  const coarse =
    typeof win.matchMedia === "function" && win.matchMedia("(pointer: coarse)").matches;
  if (w < 640) return "phone";
  if (w < 1024 && coarse) return "tablet";
  return "desktop";
}

/** Read `env(safe-area-inset-*)` if the document exposes them, else zeros. */
function readSafeArea(): SafeAreaInsets {
  if (typeof document === "undefined" || typeof getComputedStyle !== "function") return NO_INSETS;
  const probe = document.documentElement;
  const cs = getComputedStyle(probe);
  const px = (v: string) => {
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  return {
    top: px(cs.getPropertyValue("--safe-top")),
    bottom: px(cs.getPropertyValue("--safe-bottom")),
    left: px(cs.getPropertyValue("--safe-left")),
    right: px(cs.getPropertyValue("--safe-right")),
  };
}

/**
 * Detect the current platform. Pure and synchronous — safe to call at module
 * load and in tests by stubbing `window.omniDesktop` / `window.Capacitor`.
 */
export function detectPlatform(win: Window | undefined = typeof window !== "undefined" ? window : undefined): PlatformDescriptor {
  // 1) Electron — the preload injected window.omniDesktop.
  if (win?.omniDesktop) {
    const os = nodePlatformToOs(String(win.omniDesktop.os));
    return {
      host: "electron",
      formFactor: "desktop",
      isNative: true,
      os,
      safeArea: NO_INSETS,
      capabilities: resolveCapabilities("electron"),
    };
  }

  // 2) Capacitor native WebView — iOS or Android.
  const cap = win?.Capacitor;
  if (cap?.isNativePlatform?.()) {
    const platform = cap.getPlatform?.() ?? "android";
    const host: OmniHost = platform === "ios" ? "ios" : "android";
    const ff = formFactorFromViewport(win);
    return {
      host,
      formFactor: ff === "desktop" ? "phone" : ff,
      isNative: true,
      os: host,
      safeArea: readSafeArea(),
      capabilities: resolveCapabilities(host),
    };
  }

  // 3) Plain web browser.
  const ua = win?.navigator?.userAgent ?? "";
  return {
    host: "web",
    formFactor: formFactorFromViewport(win),
    isNative: false,
    os: osFromUserAgent(ua),
    safeArea: NO_INSETS,
    capabilities: resolveCapabilities("web"),
  };
}

/** A stable default used for SSR / first render before detection settles. */
export const WEB_DEFAULT: PlatformDescriptor = {
  host: "web",
  formFactor: "desktop",
  isNative: false,
  os: "unknown",
  safeArea: NO_INSETS,
  capabilities: resolveCapabilities("web"),
};
